import { useState, useCallback, useEffect } from 'react';

/**
 * Crisis keywords by language (mirrors server-side crisis-detection.ts)
 * Keep this synchronized with the server-side list.
 */
const CRISIS_KEYWORDS: Record<string, string[]> = {
  en: [
    'suicide', 'suicidal', 'self-harm', 'self harm', 'kill myself',
    'end my life', 'want to die', 'hurt myself', 'self-injur',
    'overdose', 'hang myself',
  ],
  zh: ['自杀', '自杀倾向', '自残', '我想死', '想自杀', '结束生命', '不想活了', '轻生', '寻死', '自尽'],
  hi: ['आत्महत्या', 'खुद को नुकसान', 'खुदको चोट', 'मरना चाहता', 'मरना चाहती', 'जीवन समाप्त', 'जीने का कोई कारण नहीं', 'खुद को मार'],
  es: [
    'suicidio', 'suicida', 'autolesion', 'auto lesión', 'matarme',
    'terminar con mi vida', 'quiero morir', 'herir myself', 'no quiero vivir',
  ],
  ar: ['انتحار', 'أنتحر', 'الانتحار', 'ايذاء النفس', 'إيذاء النفس', 'أريد أن أموت', 'اريد الموت', 'انهاء حياتي', 'إنهاء حياتي'],
  fr: [
    'suicide', 'suicidaire', 'automutilation', 'auto-mutilation', 'me tuer',
    'me suicider', 'finir ma vie', 'envie de mourir', 'me faire du mal', 'se donner la mort',
  ],
  bn: ['আত্মহত্যা', 'খুন করা', 'মরে যেতে চাই', 'জীবন শেষ', 'বাঁচতে চাই না', 'নিজে কে কাটা', 'আত্মহত্যা করা', 'মৃত্যু কামনা'],
  pt: [
    'suicídio', 'suicida', 'auto-mutilação', 'automutilação', 'me matar',
    'terminar minha vida', 'quero morrer', 'me machucar', 'não quero viver', 'tirar minha vida',
  ],
  ru: [
    'суицид', 'самоубийств', 'самоубийство', 'покончить с собой', 'хочу умереть',
    'хочу смерти', 'навредить себе', 'самоповреждение', 'жизнь не имеет смысла', 'не хочу жить', 'свести счеты с жизнью',
  ],
  de: ['suizid', 'suizidal', 'selbstmord', 'selbstverletzung', 'mich umbringen', 'ich will sterben', 'will nicht mehr leben', 'mir das leben nehmen'],
  it: ['suicidarmi', 'suicidarsi', 'autolesionismo', 'voglio morire', 'togliermi la vita', 'uccidermi', 'non voglio più vivere', 'farmi del male', 'ammazzarmi'],
  ja: ['自殺', '死にたい', '自傷', '消えたい', '命を絶', '自殺したい', '生きていたくない'],
  ko: ['자살', '죽고 싶', '자해', '살고 싶지 않', '극단적 선택', '목숨을 끊'],
  fa: ['خودکشی', 'خودزنی', 'می‌خواهم بمیرم', 'میخواهم بمیرم', 'نمی‌خواهم زنده بمانم', 'نمیخواهم زنده بمانم'],
  id: ['bunuh diri', 'ingin mati', 'mengakhiri hidup', 'tidak ingin hidup', 'menyakiti diri sendiri'],
  ms: ['mahu bunuh diri', 'hendak bunuh diri', 'menamatkan nyawa', 'tidak mahu hidup', 'mencederakan diri'],
  ne: ['मर्न चाहन्छु', 'मर्न चाहन्छ', 'बाँच्न चाहन्न', 'आत्महत्या गर्न', 'आफूलाई मार्न'],
  pl: ['samobójstwo', 'samobójcze', 'chcę umrzeć', 'chce umrzeć', 'nie chcę żyć', 'nie chce zyc', 'odebrać sobie życie', 'samookaleczenie'],
  ro: ['sinucidere', 'sinucid', 'vreau să mor', 'vreau sa mor', 'să-mi iau viața', 'sa-mi iau viata', 'nu mai vreau să trăiesc', 'automutilare'],
  sr: ['samoubistvo', 'hoću da umrem', 'hocu da umrem', 'ne želim da živim', 'ne zelim da zivim', 'самоубиство', 'хоћу да умрем', 'не желим да живим'],
  sv: ['självmord', 'jag vill dö', 'ta mitt liv', 'orkar inte leva', 'skada mig själv'],
  tr: ['intihar', 'ölmek istiyorum', 'olmek istiyorum', 'kendimi öldürmek', 'yaşamak istemiyorum', 'yasamak istemiyorum'],
};

/**
 * Negative context patterns that indicate false positives
 * These suggest the user is discussing crisis topics academically or hypothetically
 */
const NEGATIVE_CONTEXT_PATTERNS = [
  /\bsuicide prevention\b/i,
  /\bsuicide awareness\b/i,
  /\bstudying suicide\b/i,
  /\bresearch on suicide\b/i,
  /\bsuicide rate\b/i,
  /\bsuicide statistics\b/i,
  /\barticle about\b/i,
  /\bnews article\b/i,
  /\bmovie about\b/i,
  /\bbook about\b/i,
  /\bwriting a paper\b/i,
  /\bfor my class\b/i,
  /\bhomework about\b/i,
  /\bsuicide bomber\b/i, // Completely different context
];

/**
 * Crisis hotline resources by language (mirrors server-side)
 */
const CRISIS_HOTLINES: Record<string, { name: string; phone: string; website: string; availability: string }> = {
  en: {
    name: '988 Suicide & Crisis Lifeline',
    phone: '988 (call/text)',
    website: 'https://988lifeline.org',
    availability: '24/7, free, confidential',
  },
  zh: {
    name: 'Beijing Suicide Research and Prevention Center',
    phone: '800-810-1117',
    website: 'https://crisis.org.cn',
    availability: '24/7',
  },
  hi: {
    name: 'AASRA',
    phone: '+91-22-27546669',
    website: 'https://aasra.info',
    availability: '24x7, India',
  },
  es: {
    name: 'Teléfono de la Esperanza',
    phone: '+34 717 003 717',
    website: 'https://telefonodelaesperanza.org',
    availability: 'Spain',
  },
  ar: {
    name: 'National Mental Support Line',
    phone: '800 4673 (UAE) / 800 122 2777 (Saudi)',
    website: 'https://icarewellbeing.com',
    availability: '',
  },
  fr: {
    name: 'SOS Amitié',
    phone: '09 72 39 40 50',
    website: 'https://sosamitie.org',
    availability: '24/7, France',
  },
  bn: {
    name: 'Kaan Pete Roi',
    phone: '01779554391',
    website: 'https://kaanpeteroi.org',
    availability: '24/7, Bangladesh',
  },
  pt: {
    name: 'SOS Voz Amiga',
    phone: '+351 213 544 545',
    website: 'https://sosvozamiga.org',
    availability: 'Portugal',
  },
  ru: {
    name: 'EMERCOM of Russia',
    phone: '+7 495 400 99 99',
    website: 'https://emercor.gov.ru',
    availability: '24/7',
  },
  de: {
    name: 'Telefonseelsorge',
    phone: '0800 111 0 111 / 0800 111 0 222 / 116 123',
    website: 'https://www.telefonseelsorge.de',
    availability: '24/7, Germany',
  },
  it: {
    name: 'Telefono Amico',
    phone: '02 99777',
    website: 'https://telefonoamicocevita.it',
    availability: '24/7, Italy',
  },
  ja: {
    name: 'いのちSOS',
    phone: '0120-061-338',
    website: 'https://www.lifelink.or.jp/inochisos/',
    availability: '24/7, Japan',
  },
  ko: {
    name: '자살예방상담전화',
    phone: '109',
    website: 'https://www.129.go.kr/109/',
    availability: '24/7, South Korea',
  },
  fa: {
    name: 'صدای مشاور',
    phone: '1480',
    website: 'https://www.behzisti.ir',
    availability: 'Iran',
  },
  id: {
    name: 'SEJIWA',
    phone: '119 ext. 8',
    website: 'https://www.kemkes.go.id',
    availability: 'Indonesia',
  },
  ms: {
    name: 'Talian HEAL',
    phone: '15555',
    website: 'https://www.moh.gov.my',
    availability: 'Malaysia',
  },
  ne: {
    name: 'National Suicide Prevention Helpline',
    phone: '1166',
    website: 'https://www.iom.edu.np',
    availability: 'Nepal',
  },
  pl: {
    name: 'Kryzysowy Telefon Zaufania',
    phone: '116 123',
    website: 'https://116123.pl',
    availability: 'Poland',
  },
  ro: {
    name: 'depreHUB',
    phone: '0374 456 420',
    website: 'https://deprehub.ro',
    availability: '24/7, Romania',
  },
  sr: {
    name: 'Centar Srce',
    phone: '0800 309 309',
    website: 'https://www.centarsrce.org',
    availability: 'Serbia',
  },
  sv: {
    name: 'Mind Självmordslinjen',
    phone: '90101',
    website: 'https://mind.se',
    availability: 'Sweden',
  },
  tr: {
    name: 'Alo 183 Sosyal Destek',
    phone: '183',
    website: 'https://www.aile.gov.tr',
    availability: 'Turkey',
  },
};

export interface CrisisDetectionResult {
  isCrisis: boolean;
  language: string | null;
  keywords: string[];
  isFalsePositive: boolean;
  hotline: typeof CRISIS_HOTLINES[string] | null;
}

export interface UseCrisisDetectionOptions {
  enabled?: boolean;
  onCrisisDetected?: (result: CrisisDetectionResult) => void;
  sessionId?: string;
}

/**
 * Detect language from crisis keywords. Longer matches win so a shared
 * token such as "suicide" does not override a more specific phrase.
 */
function detectLanguage(text: string): string | null {
  const lowered = text.toLowerCase();
  let bestLang: string | null = null;
  let bestScore = 0;

  for (const [lang, keywords] of Object.entries(CRISIS_KEYWORDS)) {
    let score = 0;
    for (const kw of keywords) {
      if (lowered.includes(kw.toLowerCase())) score += kw.length;
    }
    if (score > bestScore) {
      bestScore = score;
      bestLang = lang;
    }
  }

  return bestLang;
}

/**
 * Check if text matches false positive patterns
 */
function isFalsePositive(text: string): boolean {
  return NEGATIVE_CONTEXT_PATTERNS.some(pattern => pattern.test(text));
}

/**
 * Find matching crisis keywords in text
 */
function findKeywords(text: string, language: string): string[] {
  const keywords = CRISIS_KEYWORDS[language] || CRISIS_KEYWORDS.en;
  const textLower = text.toLowerCase();
  return keywords.filter(kw => textLower.includes(kw.toLowerCase()));
}

/**
 * Get hotline for language
 */
function getHotline(language: string): typeof CRISIS_HOTLINES[string] | null {
  return CRISIS_HOTLINES[language] || CRISIS_HOTLINES.en || null;
}

/**
 * useCrisisDetection Hook
 *
 * Client-side crisis keyword detection with:
 * - Multilingual support (22 languages)
 * - Contextual filtering to reduce false positives
 * - Language-specific hotline resources
 * - Privacy-first (detection happens in browser)
 *
 * @example
 * ```tsx
 * const { checkMessage, hasOptedIn, showSafetyModal } = useCrisisDetection({
 *   onCrisisDetected: (result) => {
 *     setShowSafetyModal(true);
 *     setHotline(result.hotline);
 *   }
 * });
 *
 * // In your send handler:
 * const handleSend = (message: string) => {
 *   checkMessage(message);
 *   // ... send to API
 * };
 * ```
 */
export function useCrisisDetection(options: UseCrisisDetectionOptions = {}) {
  const { enabled = true, onCrisisDetected } = options;

  const [hasOptedIn, setHasOptedIn] = useState(false);
  const [optInData, setOptInData] = useState<{
    language: string;
    expiresAt: string;
    hotline: any;
  } | null>(null);
  const [lastCheckTime, setLastCheckTime] = useState(0);
  const [dismissedSession, setDismissedSession] = useState(false);

  // Check if user has opted into Safety Net on mount
  useEffect(() => {
    if (!enabled) return;

    const checkOptInStatus = async () => {
      try {
        const response = await fetch('/api/crisis/opt-in', {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.data?.isOptedIn) {
            setHasOptedIn(true);
            setOptInData(data.data);
          }
        }
      } catch (error) {
        console.error('Failed to check crisis opt-in status:', error);
      }
    };

    checkOptInStatus();
  }, [enabled]);

  /**
   * Check a message for crisis keywords
   * Returns CrisisDetectionResult and triggers callback if crisis detected
   */
  const checkMessage = useCallback(
    (message: string): CrisisDetectionResult | null => {
      if (!enabled) return null;

      // Rate limit: max once per 30 seconds per session
      const now = Date.now();
      if (now - lastCheckTime < 30000) {
        return null;
      }

      // If user dismissed modal this session, don't show again
      if (dismissedSession) {
        return null;
      }

      // Detect language
      const language = detectLanguage(message);
      if (!language) {
        return null;
      }

      // Find keywords
      const keywords = findKeywords(message, language);
      if (keywords.length === 0) {
        return null;
      }

      // Check for false positives
      const isFP = isFalsePositive(message);
      if (isFP) {
        return {
          isCrisis: false,
          language,
          keywords,
          isFalsePositive: true,
          hotline: getHotline(language),
        };
      }

      // Crisis detected!
      setLastCheckTime(now);

      const result: CrisisDetectionResult = {
        isCrisis: true,
        language,
        keywords,
        isFalsePositive: false,
        hotline: getHotline(language),
      };

      // Trigger callback
      onCrisisDetected?.(result);

      // Record trigger on server (if authenticated)
      fetch('/api/crisis/trigger', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          language,
          detectedKeywords: keywords,
        }),
      }).catch(err => console.error('Failed to record crisis trigger:', err));

      return result;
    },
    [enabled, lastCheckTime, dismissedSession, onCrisisDetected]
  );

  /**
   * Register user for Safety Net
   */
  const registerSafetyNet = useCallback(
    async (data: {
      email: string;
      fullName: string;
      emergencyContact?: string;
      country?: string;
    }) => {
      const response = await fetch('/api/crisis/opt-in', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          language: optInData?.language || 'en',
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to register Safety Net');
      }

      const result = await response.json();
      setHasOptedIn(true);
      setOptInData(result.data);

      // Track analytics
      if ((window as any).analytics) {
        (window as any).analytics.track('crisis_safety_net_registered', {
          language: result.data.language,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [optInData]
  );

  /**
   * Remove Safety Net registration
   */
  const removeSafetyNet = useCallback(async () => {
    const response = await fetch('/api/crisis/opt-in', {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to remove Safety Net');
    }

    setHasOptedIn(false);
    setOptInData(null);

    if ((window as any).analytics) {
      (window as any).analytics.track('crisis_safety_net_removed', {
        timestamp: new Date().toISOString(),
      });
    }
  }, []);

  /**
   * Mark modal as dismissed for this session
   */
  const dismissModal = useCallback(() => {
    setDismissedSession(true);
  }, []);

  /**
   * Reset dismissed state (call when session ends)
   */
  const resetDismissed = useCallback(() => {
    setDismissedSession(false);
  }, []);

  return {
    // Methods
    checkMessage,
    registerSafetyNet,
    removeSafetyNet,
    dismissModal,
    resetDismissed,

    // State
    hasOptedIn,
    optInData,
    dismissedSession,

    // Hotline for current language
    getHotline: (language: string) => getHotline(language),
  };
}

export default useCrisisDetection;
